export interface WebSocketLifecycleSocket {
  readonly readyState: number;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent<string>) => void) | null;
  onerror: ((event: Event) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  send(data: string): void;
}

interface BindCurrentWebSocketHandlersOptions<TReceive, TSocket extends WebSocketLifecycleSocket> {
  socket: TSocket;
  isCurrent: (socket: TSocket) => boolean;
  deserialize: (data: string) => TReceive;
  onOpen: (event: Event) => void;
  onMessage: (data: TReceive, event: MessageEvent<string>) => void;
  onError: (event: Event) => void;
  onClose: (event: CloseEvent) => void;
  onParseError?: (error: unknown) => void;
}

/**
 * Bind one socket to the active connection lifecycle.
 *
 * Browser events can arrive after React cleanup has replaced a connecting
 * socket. Those stale events must not change connection state or start a
 * reconnect for the replacement socket.
 */
export function bindCurrentWebSocketHandlers<
  TReceive,
  TSocket extends WebSocketLifecycleSocket,
>({
  socket,
  isCurrent,
  deserialize,
  onOpen,
  onMessage,
  onError,
  onClose,
  onParseError,
}: BindCurrentWebSocketHandlersOptions<TReceive, TSocket>): void {
  socket.onopen = (event) => {
    if (!isCurrent(socket)) return;
    onOpen(event);
  };

  socket.onmessage = (event) => {
    if (!isCurrent(socket)) return;
    try {
      onMessage(deserialize(event.data), event);
    } catch (error) {
      onParseError?.(error);
    }
  };

  socket.onerror = (event) => {
    if (!isCurrent(socket)) return;
    onError(event);
  };

  socket.onclose = (event) => {
    if (!isCurrent(socket)) return;
    onClose(event);
  };
}
